import { useDroppable } from "@dnd-kit/core";
import { ChevronsDownUp, ChevronsUpDown, FolderPlus } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode
} from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { LoadingState } from "@/components/brand";
import { Button } from "@/components/ui/button";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger
} from "@/components/ui/context-menu";
import {
  DropdownMenuContent,
  DropdownMenuItem
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import type { ZipRequest } from "../../caseFiles/api/filesystemApi";
import { PaneHeader } from "../components/PaneHeader";
import { PaneMaximizeButton } from "../layout/PaneMaximizeButton";
import { toggleFocusFromHeader } from "../layout/paneShortcuts";
import { useCaseIde } from "../context/CaseIdeContext";
import { useCaseData } from "../data/CaseDataContext";
import { AddToCaseFilesDialog } from "../files/AddToCaseFilesDialog";
import {
  copiesBySource,
  draftCopySource,
  formCopySource,
  sourceKey,
  type CopySource
} from "../files/caseFileCopies";
import { nodesInScope, type ZipSummary } from "../files/downloadModel";
import { useExplorerDnd } from "../explorer/ExplorerDnd";
import { useHeaderFit } from "../files/headerOverflow";
import { contextMenuKit, dropdownMenuKit } from "../files/menuKit";
import { TreeOverflowMenu } from "../files/TreeOverflowMenu";
import { ancestorsOf, rangeBetween, stepRow } from "../files/treeRows";
import { useZipDownload } from "../files/useZipDownload";
import { artifactKey } from "../types";
import {
  WorkflowsMenuItem,
  WorkflowsSparkle
} from "../workflows/components/WorkflowsSparkle";
import type { FormOption } from "../workflows/types";
import { AddFormDialog } from "./AddFormDialog";
import { NewDraftMenu } from "./NewDraftMenu";
import type { DraftTypeOption, WsNode } from "./types";
import {
  buildWorkspaceRows,
  containsPacket,
  folderPaths,
  formName,
  rowTitle,
  subtreeContainsKind,
  type WsRow
} from "./workspaceModel";
import { canRenameRow, WorkspaceRow } from "./WorkspaceRow";
import {
  WorkProductsMenuItems,
  type WorkProductsMenuActions,
  type WorkProductsMenuModel
} from "./workProductMenus";

const ROOT_NAME = "Work Products";
const NEW_FOLDER_NAME = "New folder";

type HeaderAction = "prepare" | "expand";
/**
 * The toolbar's movable actions, most important first. As the explorer
 * narrows the last ones move into "…" one by one. New Folder, New Draft, "…"
 * and Maximize always stay.
 */
const HEADER_PRIORITY: readonly HeaderAction[] = ["prepare", "expand"];

const IconButton: FC<{
  label: string;
  onClick: () => void;
  children: ReactNode;
}> = ({ label, onClick, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className="size-6"
        aria-label={label}
        onClick={onClick}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom">{label}</TooltipContent>
  </Tooltip>
);

/**
 * The left-bottom pane: what the case has produced (drafts, packet, forms,
 * research memos) as a tree the attorney organises like Case Files: folders,
 * drag and drop, rename, delete, new draft. Each item opens in the center as a
 * tab. The structure is the workspace tree; titles and statuses come from the
 * artifacts themselves.
 */
export const CaseWorkspaceTree: FC = () => {
  const {
    individualId,
    open,
    activeKey,
    requestMention,
    requestPrompt,
    pruneTabs,
    renameTab,
    invalidate
  } = useCaseIde();
  const { caseId, workspace: ws, fs } = useCaseData();
  const { tree } = ws;
  const zip = useZipDownload();
  const headerRef = useRef<HTMLDivElement>(null);
  const headerFit = useHeaderFit({
    headerRef,
    priority: HEADER_PRIORITY,
    // New Folder, New Draft, "…" and Maximize.
    fixedCount: 4
  });
  const shows = (id: HeaderAction) => headerFit.visible.has(id);
  const overflows = (id: HeaderAction) => !headerFit.visible.has(id);
  const [promoteSource, setPromoteSource] = useState<CopySource | null>(null);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const anchorRef = useRef<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [menuRow, setMenuRow] = useState<WsRow | null>(null);
  const [rowMenuId, setRowMenuId] = useState<string | null>(null);
  const rowMenuIdRef = useRef<string | null>(null);
  rowMenuIdRef.current = rowMenuId;
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);
  /** Add Form's target folder while its picker is open (`null`: the Forms folder). */
  const [addFormTo, setAddFormTo] = useState<{
    parentId: string | null;
  } | null>(null);
  const seededRoot = useRef(false);
  const treeRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const explorer = useExplorerDnd();
  const { setNodeRef: setRootDropRef } = useDroppable({
    id: "workspace:root",
    data: { tree: "workspace", root: true }
  });
  useEffect(
    () =>
      explorer.registerTree("workspace", {
        selection: () => selectedRef.current,
        expand: (parentId) =>
          setExpanded((current) => new Set([...current, parentId])),
        cancelRename: () => setRenamingId(null)
      }),
    [explorer]
  );

  const rows = useMemo(
    () => buildWorkspaceRows(tree.nodes, ws.sources, expanded),
    [tree.nodes, ws.sources, expanded]
  );
  const byId = useMemo(
    () => new Map(tree.nodes.map((n) => [n.id, n])),
    [tree.nodes]
  );
  const rowById = useMemo(
    () => new Map(rows.map((r) => [r.node.id, r])),
    [rows]
  );
  const paths = useMemo(() => folderPaths(tree.nodes), [tree.nodes]);
  const folders = useMemo(
    () =>
      tree.nodes
        .filter((n) => n.kind === "folder" && !n.is_deleted)
        .sort((a, b) =>
          (paths.get(a.id) ?? "").localeCompare(paths.get(b.id) ?? "")
        ),
    [tree.nodes, paths]
  );

  // Open the top level once the tree first loads. A section folder the server
  // creates later (the "Plans" folder appears with the first saved plan) opens
  // too, so what was just filed is visible.
  const seenSlotFolders = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (tree.isLoading || tree.nodes.length === 0) return;
    const slotFolders = tree.nodes.filter(
      (n) => n.kind === "folder" && !n.parent_id && n.slot && !n.is_deleted
    );
    if (!seededRoot.current) {
      seededRoot.current = true;
      slotFolders.forEach((n) => seenSlotFolders.current.add(n.id));
      setExpanded(
        new Set(
          tree.nodes
            .filter((n) => n.kind === "folder" && !n.parent_id)
            .map((n) => n.id)
        )
      );
      return;
    }
    const fresh = slotFolders.filter((n) => !seenSlotFolders.current.has(n.id));
    if (!fresh.length) return;
    fresh.forEach((n) => seenSlotFolders.current.add(n.id));
    setExpanded((s) => new Set([...s, ...fresh.map((n) => n.id)]));
  }, [tree.isLoading, tree.nodes]);

  // Keep open tabs consistent with what still exists / how it is now titled.
  useEffect(() => {
    if (!ws.complete) return;
    const known = new Map<string, string>();
    ws.sections.forEach((s) =>
      s.items.forEach((i) =>
        known.set(artifactKey(i.artifact), i.artifact.title)
      )
    );
    pruneTabs(
      (a) =>
        a.kind === "file" ||
        a.kind === "card" ||
        a.kind === "settings" ||
        a.kind === "plan" ||
        a.kind === "key_facts" ||
        a.kind === "time" ||
        a.kind === "company" ||
        a.kind === "workflows" ||
        known.has(artifactKey(a))
    );
    known.forEach((title, key) => renameTab(key, title));
  }, [ws.complete, ws.sections, pruneTabs, renameTab]);

  const toggle = useCallback((id: string) => {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const openRow = useCallback(
    (row: WsRow) => {
      if (row.node.kind === "folder") {
        toggle(row.node.id);
        return;
      }
      if (row.item) open(row.item.artifact);
    },
    [open, toggle]
  );

  const onRowClick = useCallback(
    (row: WsRow, e: MouseEvent) => {
      const { id } = row.node;
      setFocusedId(id);
      if (e.shiftKey && anchorRef.current) {
        setSelected(new Set(rangeBetween(rows, anchorRef.current, id)));
      } else if (e.metaKey || e.ctrlKey) {
        setSelected((s) => {
          const next = new Set(s);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        });
        anchorRef.current = id;
      } else {
        setSelected(new Set([id]));
        anchorRef.current = id;
        if (row.item) open(row.item.artifact);
      }
    },
    [open, rows]
  );

  const onKeyDown = useCallback(
    (row: WsRow, e: KeyboardEvent) => {
      const { id } = row.node;
      const focusRow = (nextId: string | null) => {
        if (!nextId) return;
        setFocusedId(nextId);
        setSelected(new Set([nextId]));
        anchorRef.current = nextId;
        requestAnimationFrame(() => {
          treeRef.current
            ?.querySelector<HTMLElement>(`[data-node-id="${nextId}"]`)
            ?.focus();
        });
      };
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          focusRow(stepRow(rows, id, 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          focusRow(stepRow(rows, id, -1));
          break;
        case "ArrowRight":
          e.preventDefault();
          if (row.node.kind === "folder" && !row.expanded) toggle(id);
          else focusRow(stepRow(rows, id, 1));
          break;
        case "ArrowLeft":
          e.preventDefault();
          if (row.node.kind === "folder" && row.expanded) toggle(id);
          else if (row.node.parent_id) focusRow(row.node.parent_id);
          break;
        case "Enter":
          e.preventDefault();
          openRow(row);
          break;
        case "F2":
          e.preventDefault();
          if (canRenameRow(row)) setRenamingId(id);
          break;
        case "Backspace":
        case "Delete": {
          e.preventDefault();
          const ids = selected.size ? [...selected] : [id];
          if (!containsPacket(tree.nodes, ids)) setConfirmDelete(ids);
          break;
        }
        default:
      }
    },
    [openRow, rows, selected, toggle, tree.nodes]
  );

  const onActionsOpenChange = useCallback((row: WsRow, open: boolean) => {
    if (!open) {
      setRowMenuId((current) => (current === row.node.id ? null : current));
      setMenuRow((current) =>
        current?.node.id === row.node.id ? null : current
      );
      return;
    }
    setRowMenuId(row.node.id);
    setMenuRow(row);
    setFocusedId(row.node.id);
    setSelected((current) => {
      if (current.has(row.node.id)) return current;
      anchorRef.current = row.node.id;
      return new Set([row.node.id]);
    });
  }, []);

  // ---- mutations -----------------------------------------------------------
  const reveal = useCallback(
    (parentId: string | null) => {
      if (!parentId) return;
      setExpanded(
        (s) => new Set([...s, parentId, ...ancestorsOf(tree.nodes, parentId)])
      );
    },
    [tree.nodes]
  );
  const createFolder = useCallback(
    async (parentId: string | null) => {
      const node = await tree.createFolder(NEW_FOLDER_NAME, parentId);
      if (!node) return;
      reveal(parentId);
      setSelected(new Set([node.id]));
      setFocusedId(node.id);
      setRenamingId(node.id);
    },
    [tree, reveal]
  );
  const createDraft = useCallback(
    async (parentId: string | null, option?: DraftTypeOption) => {
      const typed = option && option.draft_type !== "custom" ? option : null;
      const created = await tree.createDraft(
        parentId,
        null,
        option?.draft_type ?? null
      );
      if (!created) return;
      reveal(created.reused ? created.node.parent_id : parentId);
      setSelected(new Set([created.node.id]));
      setFocusedId(created.node.id);
      // The draft list has to know about it before the row can show a title.
      await ws.refetch(["drafts"]);
      if (!typed) {
        setRenamingId(created.node.id);
        return;
      }
      open({
        kind: "draft",
        id: created.draft.draft_id,
        title: created.draft.title || typed.label
      });
      const prompt = created.intake_prompt ?? typed.intake_prompt;
      if (prompt) requestPrompt(prompt);
    },
    [tree, reveal, ws, open, requestPrompt]
  );
  const addForm = useCallback(
    async (parentId: string | null, form: FormOption, label: string | null) => {
      const created = await tree.createForm(form.formNumber, label, parentId);
      if (!created) return;
      reveal(created.node.parent_id);
      setSelected(new Set([created.node.id]));
      setFocusedId(created.node.id);
      anchorRef.current = created.node.id;
      await ws.refetch(["forms"]);
      open({
        kind: "form",
        id: created.form.case_extend_uscis_filled_form_id,
        title: formName(created.form)
      });
    },
    [tree, reveal, ws, open]
  );
  const caseForms = useMemo(
    () =>
      ws.forms.map((f) => ({
        id: f.case_extend_uscis_filled_form_id,
        formNumber: f.form_number,
        label: f.label ?? null
      })),
    [ws.forms]
  );
  const commitRename = useCallback(
    async (id: string, name: string) => {
      setRenamingId(null);
      const node = byId.get(id);
      await tree.rename(id, name);
      if (node?.kind === "draft" && node.ref_id) {
        renameTab(`draft:${node.ref_id}`, name);
        // The title lives on the draft; pull it so the row and tab agree.
        await ws.refetch(["drafts"]);
      } else if (node?.kind === "plan" && node.ref_id) {
        renameTab(`plan:${node.ref_id}`, name);
        await ws.refetch(["plans"]);
        // The open plan pane follows the same document.
        invalidate(["plans"]);
      }
    },
    [byId, tree, renameTab, ws, invalidate]
  );
  const deleteNodes = useCallback(
    async (ids: string[]) => {
      setConfirmDelete(null);
      await tree.remove(ids);
      setSelected((s) => new Set([...s].filter((x) => !ids.includes(x))));
      // Drafts and forms inside were deleted too; refresh so tabs get pruned.
      // Plans inside were only taken out of the workspace (their panes stay).
      await ws.refetch(["drafts", "forms", "plans"]);
      invalidate(["plans"]);
    },
    [tree, ws, invalidate]
  );

  // ---- downloads and copies to Case Files --------------------------------------
  const copies = useMemo(() => copiesBySource(fs.nodes), [fs.nodes]);
  const copyFor = useCallback(
    (row: WsRow): { label: string | null } | null => {
      if (!row.item || (row.node.kind !== "draft" && row.node.kind !== "form"))
        return null;
      const copy = copies.get(sourceKey(row.node.kind, row.node.ref_id ?? ""));
      if (!copy) return null;
      return {
        label:
          copy.packet_inclusion === "excluded"
            ? null
            : fs.labels[copy.id] ?? null
      };
    },
    [copies, fs.labels]
  );
  const copyByRow = useMemo(
    () => new Map(rows.map((r) => [r.node.id, copyFor(r)])),
    [rows, copyFor]
  );
  const copySourceFor = useCallback(
    (row: WsRow): CopySource | null => {
      const ref = row.node.ref_id;
      if (!ref) return null;
      if (row.node.kind === "draft") {
        const draft = ws.drafts.find((d) => d.draft_id === ref);
        return draft?.docx_storage_path ? draftCopySource(draft) : null;
      }
      if (row.node.kind === "form") {
        const form = ws.forms.find(
          (f) => f.case_extend_uscis_filled_form_id === ref
        );
        return form ? formCopySource(form) : null;
      }
      return null;
    },
    [ws.drafts, ws.forms]
  );
  const summarizeRequest = useCallback(
    (request: ZipRequest): ZipSummary => {
      const drafts = new Map(ws.drafts.map((d) => [d.draft_id, d]));
      const forms = new Set(
        ws.forms.map((f) => f.case_extend_uscis_filled_form_id)
      );
      const plans = new Set(ws.plans.map((p) => p.id));
      const covered = nodesInScope(tree.nodes, request).filter((n) => {
        const ref = n.ref_id ?? "";
        if (n.kind === "packet")
          return request.include_packet !== false && !!ws.packet?.file_path;
        if (n.kind === "draft") return !!drafts.get(ref)?.docx_storage_path;
        if (n.kind === "form") return forms.has(ref);
        return n.kind === "plan" && plans.has(ref);
      });
      return { files: covered.length, bytes: null };
    },
    [tree.nodes, ws.drafts, ws.forms, ws.plans, ws.packet]
  );
  const focusFolder = useMemo(() => {
    const first = byId.get([...selected][0] ?? "");
    const folder = first
      ? first.kind === "folder"
        ? first
        : first.parent_id
        ? byId.get(first.parent_id)
        : undefined
      : undefined;
    return folder ? { id: folder.id, name: folder.name } : null;
  }, [selected, byId]);

  const menuIds = menuRow
    ? selected.has(menuRow.node.id)
      ? [...selected]
      : [menuRow.node.id]
    : [];
  const menuNodes = menuIds
    .map((id) => byId.get(id))
    .filter(Boolean) as WsNode[];
  const menuHasPacket = containsPacket(tree.nodes, menuIds);
  // Forms are added from the catalog, not drafted: the Forms folder and
  // everything in it offer Add Form instead of New draft.
  const formsFolderId = tree.nodes.find(
    (n) => n.kind === "folder" && n.slot === "forms" && !n.is_deleted
  )?.id;
  const menuInForms =
    !!menuRow &&
    !!formsFolderId &&
    (menuRow.node.id === formsFolderId ||
      ancestorsOf(tree.nodes, menuRow.node.id).includes(formsFolderId));
  const menuFolderId = menuRow
    ? menuRow.node.kind === "folder"
      ? menuRow.node.id
      : menuRow.node.parent_id ?? null
    : null;
  // With nothing expanded, the button flips to Expand all (expands every folder).
  const allCollapsed = !folders.some((f) => expanded.has(f.id));
  const expandLabel = allCollapsed ? "Expand all" : "Collapse all";
  const toggleExpandAll = () =>
    setExpanded(allCollapsed ? new Set(folders.map((f) => f.id)) : new Set());

  const confirmTitle = (() => {
    if (!confirmDelete) return "";
    if (confirmDelete.length === 1) {
      const row = rowById.get(confirmDelete[0]);
      const node = byId.get(confirmDelete[0]);
      const name = row ? rowTitle(row) : node?.name || "item";
      return `"${name}"`;
    }
    return `${confirmDelete.length} items`;
  })();
  const confirmHasArtifacts = confirmDelete
    ? subtreeContainsKind(tree.nodes, confirmDelete, "draft") ||
      subtreeContainsKind(tree.nodes, confirmDelete, "form")
    : false;
  const menuModel: WorkProductsMenuModel = {
    menuRow,
    menuIds,
    menuNodes,
    folders,
    nodes: tree.nodes,
    paths,
    menuInForms,
    menuFolderId,
    menuHasPacket,
    canCopyToCaseFiles:
      !!menuRow && menuIds.length === 1 && !!copySourceFor(menuRow),
    rootName: ROOT_NAME
  };
  const menuActions: WorkProductsMenuActions = {
    openRow,
    mention: (row) => {
      if (row.item) requestMention(row.item.artifact);
    },
    createFolder: (parentId) => {
      void createFolder(parentId);
    },
    addForm: (parentId) => setAddFormTo({ parentId }),
    createDraft: (parentId) => {
      void createDraft(parentId);
    },
    rename: setRenamingId,
    move: (ids, parentId) => {
      void tree.move(ids, { target_parent_id: parentId });
    },
    addToCaseFiles: () => {
      if (!menuRow) return;
      const source = copySourceFor(menuRow);
      if (source) setPromoteSource(source);
    },
    deleteNodes: setConfirmDelete,
    refresh: () => {
      void ws.refetch();
    }
  };

  return (
    <div className="explorer-card flex h-full min-h-0 flex-col">
      <div className="relative shrink-0" ref={headerRef}>
        <PaneHeader
          title={ROOT_NAME}
          onDoubleClick={(e) => toggleFocusFromHeader(e, "workspace")}
          actions={
            <>
              {shows("prepare") ? <WorkflowsSparkle /> : null}
              <IconButton label="New folder" onClick={() => createFolder(null)}>
                <FolderPlus className="size-3.5" />
              </IconButton>
              <NewDraftMenu
                individualId={individualId}
                onPick={(option) => void createDraft(null, option)}
              />
              {shows("expand") ? (
                <IconButton label={expandLabel} onClick={toggleExpandAll}>
                  {allCollapsed ? (
                    <ChevronsUpDown className="size-3.5" />
                  ) : (
                    <ChevronsDownUp className="size-3.5" />
                  )}
                </IconButton>
              ) : null}
              <TreeOverflowMenu
                name={ROOT_NAME}
                testId="work-products-more"
                hasItems={rows.length > 0}
                onRefresh={() => void ws.refetch()}
                download={{
                  tree: "work",
                  selectedIds: [...selected],
                  folder: focusFolder,
                  summarize: summarizeRequest,
                  packetHint: ws.packet?.total_pages
                    ? `${ws.packet.total_pages} pages`
                    : null,
                  onDownload: (download) => void zip(download)
                }}
                overflow={
                  headerFit.overflow.length ? (
                    <>
                      {overflows("prepare") ? (
                        <WorkflowsMenuItem testId="work-products-more-prepare" />
                      ) : null}
                      {overflows("expand") ? (
                        <DropdownMenuItem
                          onSelect={toggleExpandAll}
                          data-testid="work-products-more-expand"
                        >
                          {allCollapsed ? (
                            <ChevronsUpDown />
                          ) : (
                            <ChevronsDownUp />
                          )}
                          {expandLabel}
                        </DropdownMenuItem>
                      ) : null}
                    </>
                  ) : null
                }
              />
              <PaneMaximizeButton focus="workspace" />
            </>
          }
        />
      </div>

      <ContextMenu
        onOpenChange={(open) => {
          if (!open && !rowMenuIdRef.current) setMenuRow(null);
        }}
      >
        <ContextMenuTrigger asChild>
          <div
            ref={(element) => {
              treeRef.current = element;
              setRootDropRef(element);
            }}
            role="tree"
            tabIndex={0}
            aria-label={ROOT_NAME}
            className={cn(
              "relative min-h-0 flex-1 overflow-y-auto py-1 [scrollbar-width:thin]",
              explorer.isRootTarget("workspace") &&
                "bg-accent-soft ring-1 ring-inset ring-primary"
            )}
            onContextMenu={(e) => {
              if (!(e.target as HTMLElement).closest("[data-node-id]"))
                setMenuRow(null);
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setSelected(new Set());
                setFocusedId(null);
              }
            }}
          >
            {ws.error ? (
              <div className="px-3 py-2 text-xs text-destructive">
                {ws.error}
              </div>
            ) : null}
            {!caseId && !ws.isLoading ? (
              <div className="px-3 py-1 text-xs text-muted-foreground">
                Forms need a case record; none found.
              </div>
            ) : null}
            {ws.isLoading && rows.length === 0 ? (
              <LoadingState
                size="md"
                text="Loading work products…"
                className="absolute inset-0"
              />
            ) : null}
            {!ws.isLoading && rows.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                Nothing here yet. Ask the agent to draft something, or use New
                draft.
              </div>
            ) : null}
            {rows.map((row) => (
              <WorkspaceRow
                key={row.node.id}
                row={row}
                caseFileCopy={copyByRow.get(row.node.id) ?? null}
                selected={
                  selected.has(row.node.id) ||
                  (!!row.item && activeKey === artifactKey(row.item.artifact))
                }
                focused={focusedId === row.node.id}
                renaming={renamingId === row.node.id}
                dropHint={explorer.dropHintFor("workspace", row.node.id)}
                dragging={explorer.isDragging(row.node.id)}
                onToggle={toggle}
                onClick={onRowClick}
                onDoubleClick={openRow}
                onContextMenu={(r) => {
                  setRowMenuId(null);
                  setMenuRow(r);
                  setFocusedId(r.node.id);
                  if (!selected.has(r.node.id)) {
                    setSelected(new Set([r.node.id]));
                    anchorRef.current = r.node.id;
                  }
                }}
                actionsOpen={rowMenuId === row.node.id}
                onActionsOpenChange={onActionsOpenChange}
                actionsMenu={
                  rowMenuId === row.node.id ? (
                    <DropdownMenuContent
                      align="end"
                      className="w-56"
                      data-testid="workspace-row-actions-menu"
                      onCloseAutoFocus={(e) => e.preventDefault()}
                    >
                      <WorkProductsMenuItems
                        kit={dropdownMenuKit}
                        model={menuModel}
                        actions={menuActions}
                      />
                    </DropdownMenuContent>
                  ) : null
                }
                onRenameCommit={commitRename}
                onRenameCancel={() => setRenamingId(null)}
                onKeyDown={onKeyDown}
              />
            ))}
          </div>
        </ContextMenuTrigger>

        <ContextMenuContent
          className="w-56"
          data-testid="workspace-context-menu"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <WorkProductsMenuItems
            kit={contextMenuKit}
            model={menuModel}
            actions={menuActions}
          />
        </ContextMenuContent>
      </ContextMenu>

      <AddFormDialog
        individualId={individualId}
        open={addFormTo !== null}
        onOpenChange={(o) => !o && setAddFormTo(null)}
        caseForms={caseForms}
        onAdd={(form, label) =>
          addForm(addFormTo?.parentId ?? null, form, label)
        }
      />

      <AddToCaseFilesDialog
        source={promoteSource}
        onOpenChange={(o) => !o && setPromoteSource(null)}
      />

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmTitle}?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmHasArtifacts
                ? "Drafts and forms inside are deleted with it, not just removed from the tree. Folders are deleted with everything inside them."
                : "Folders are deleted with everything inside them."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => confirmDelete && deleteNodes(confirmDelete)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
